/**
 * Created by PRIME AI.
 * @author Samuel Hinchliffe <samuel@primeai.co.uk, sam.hinchliffe.work@gmail.com>
 * @see    [Linkedin] {@link https://www.linkedin.com/in/samuel-hinchliffe-2bb5801a5/}
 *
 * @summary A custom class for each project, you define the app runner. You define what apps
 * it should run from the AppFactory, as the AppFactory has all the useful functions for
 * running PRIME AI Apps.
 *
 * Created at: 25/10/2023
 * @see       [PRIME-AI GITHUB] {@link https://github.com/PRIME-AI-GITHUB}
 * @see       [Website]         {@link https://www.primeai.co.uk/}
 * @copyright 2018-2023 PRIME AI LTD
 */

import UploadWindowButton from '../Components/StyleSnap/UploadWindowButton';
import UploadWindow from '../Components/StyleSnap/UploadWindow';
import { Log, App, AppNotAllowedError } from '../../modules/WidgetWorks/WidgetWorks';

// !! Extend a baseline app.
// !! App = [config, appFactory] | start? | delete | validApp [init, isAllowedToRun] | overRide | unOverride
/// !! hooks | overrideHook | preLoad | postLoad |
// !! RENAME THIS.
export default class StyleSnap extends App {
    constructor(config, appFactory) {
        super(config, appFactory);
    }

    overrideHook() {}

    preLoad() {}

    postLoad() {}

    async isAppAllowedToRun() {
        return { result: true, reason: "It's all good! But do add more conditions here..." };
    }

    async init() {
        try {
            // Run the over-ride hook.
            this.overrideHook();

            const allowedToRun = await this.isAppAllowedToRun();
            if (!allowedToRun.result) throw new Error(allowedToRun.reason);

            // Run the over-ride hook.
            this.preLoad();

            // Load App
            await this.loadApp();

            // Run the over-ride hook.
            this.postLoad();

            // !! Update Analytics for this App.
            const flagExists = localStorage.getItem('fcbp_styleSnap_oldUser');
            if (flagExists) {
                this.appFactory.analytics.registerEvent('button_display', 1, 'StyleSnap');
            } else {
                localStorage.setItem('fcbp_styleSnap_oldUser', 'true');
                this.appFactory.analytics.registerEvent('unique_visitors', 1, 'StyleSnap');
            }

            return true;
        } catch (error) {
            if (error instanceof AppNotAllowedError) {
                console.error('Failed to initialize:', error);
                console.log(...Log.w(`${this.constructor.name}`, 'ERROR', `AppNotAllowedError Failed to load 1 or more apps: ${error}`));
                return false;
            }
            this.sentry.captureException(error);
            console.error('Critical Error:', error);
            return false;
        }
    }

    async loadApp() {
        // !! UPDATE AppFactory Compiler to link 'this' as it's parent owner.
        const searchBarAppDetails = await this.appFactory.compileApp(this.config.apps.UploadWindowButton);
        const uploadAppWindowDetails = await this.appFactory.compileApp(this.config.apps.UploadWindow);

        this.searchBarApp = new UploadWindowButton(searchBarAppDetails);
        const searchBarPromise = this.searchBarApp.init();

        this.uploadWindow = new UploadWindow(uploadAppWindowDetails);
        const uploadWindowPromise = this.uploadWindow.init();

        // Set the searchBarApps Displayer to the instance of the window
        this.searchBarApp.appToDisplay = this.uploadWindow;

        // Do not continue until both apps have loaded.
        // !! Does this app need each other at loading time?
        // await Promise.all([searchBarAppDetails, uploadAppWindowDetails]);

        // Link any errors from init, to the error handler.
        // Wait for both init promises to settle
        Promise.allSettled([searchBarPromise, uploadWindowPromise]).then((results) => {
            results.forEach((result) => {
                if (result.status === 'rejected') {
                    const error = new Error(result.reason.message);
                    error.stack = result.reason.stack;
                    this.handleError(error);
                }
            });
        });
        return true;
    }
}
